import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { showHtdLoginSuccess } from '../utils/htdLoginSuccess'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import * as walletService from '../services/walletService'
import { getThemeById } from '../config/themes'
import HtdLotteryResults from '../components/lottery-result/HtdLotteryResults'
import { resolveReferralToken } from '../services/affiliateService'
import './HtdHomePage.css'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface HtdHomePageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const AFFILIATE_REFERRAL_KEY = 'affiliate_referral_code'
const REMEMBER_ME_KEY = 'rememberMe'
const SAVED_USERNAME_KEY = 'savedUsername'

const HtdHomePage: React.FC<HtdHomePageProps> = ({ themeConfig, assetConfig }) => {
  const navigate = useNavigate()
  const authenticated = authService.isAuthenticated()
  const user = authService.getCurrentUser()
  const branding = themeConfig.theme?.branding
  const siteName = branding?.siteName || 'หวยวันนี้'
  const logoUrl = branding?.logoUrl || ''

  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId]
  )

  const getAssetUrl = useCallback(
    (path: string) => {
      const base = assetConfig.CDN_BASE_URL ? `${assetConfig.CDN_BASE_URL}/${themeFolder}` : `/${themeFolder}`
      return base + (path.startsWith('/') ? path : `/${path}`)
    },
    [assetConfig.CDN_BASE_URL, themeFolder]
  )

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget
    if (el.dataset.imgFallback === '1') return
    el.dataset.imgFallback = '1'
    el.src = IMG_PLACEHOLDER
  }

  const [username, setUsername] = useState(
    () => (typeof localStorage !== 'undefined' ? localStorage.getItem(SAVED_USERNAME_KEY) || '' : '')
  )
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(
    () =>
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem(REMEMBER_ME_KEY) !== 'false' || !!localStorage.getItem(SAVED_USERNAME_KEY))
  )
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [walletData, setWalletData] = useState<walletService.WalletData | null>(null)
  const [points, setPoints] = useState(0)
  const [loadingWallet, setLoadingWallet] = useState(false)
  const [resultsVisible, setResultsVisible] = useState(false)
  const resultsRef = useRef<HTMLElement>(null)

  const isLotteryEnabled = themeConfig.settings?.lotteryEnabled !== false
  const isCasinoEnabled = themeConfig.settings?.casinoEnabled !== false
  const marqueeText =
    (themeConfig.settings?.marqueeText || '').trim() ||
    'ยินดีต้อนรับทุกท่านเข้าสู่เว็บหวยออนไลน์ มั่นคง ซื่อตรง รับประกันจ่ายจริงทุกโพย'
  const bgUrl = getAssetUrl('/img/bg.jpg')

  useEffect(() => {
    document.body.classList.add('htd-home-active')
    return () => document.body.classList.remove('htd-home-active')
  }, [])

  useEffect(() => {
    const el = resultsRef.current
    if (!el) return
    // threshold > 0 พังบนมือถือเมื่อบล็อกผลรางวัลสูงมาก (ยี่กี VIP ร้อยรอบ)
    // — ต้องเห็น 12% ของทั้ง section ซึ่งสูงกว่าจอ → ค้าง opacity:0
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setResultsVisible(true)
          io.disconnect()
        }
      },
      { threshold: 0, rootMargin: '80px 0px' }
    )
    io.observe(el)
    const fallback = window.setTimeout(() => setResultsVisible(true), 1200)
    return () => {
      io.disconnect()
      window.clearTimeout(fallback)
    }
  }, [])

  useEffect(() => {
    if (authenticated) return
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    const aff = params.get('aff')
    if (ref?.trim()) {
      resolveReferralToken(ref.trim())
        .then(({ referralCode }) => {
          sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, referralCode)
          navigate(`/register${window.location.search}`, { replace: true })
        })
        .catch(() => {})
    } else if (aff?.trim()) {
      sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, aff.trim())
      navigate(`/register${window.location.search}`, { replace: true })
    }
  }, [authenticated, navigate])

  useEffect(() => {
    if (!authenticated) return
    setLoadingWallet(true)
    Promise.all([walletService.getWallet(), authService.fetchUserInfoSummary()])
      .then(([walletRes, summary]) => {
        if (walletRes.success && walletRes.data) setWalletData(walletRes.data)
        if (summary) setPoints(summary.points ?? 0)
      })
      .finally(() => setLoadingWallet(false))
  }, [authenticated])

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const identifier = username.trim()
    if (!identifier || !password || loginLoading) return
    setLoginLoading(true)
    setLoginError('')
    try {
      const res = await authService.login({
        tenantId: themeConfig.tenantId,
        username: identifier,
        password,
        rememberMe,
      })
      if (res.success) {
        if (typeof localStorage !== 'undefined') {
          if (rememberMe) localStorage.setItem(SAVED_USERNAME_KEY, identifier)
          else localStorage.removeItem(SAVED_USERNAME_KEY)
          localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false')
        }
        await showHtdLoginSuccess({
          username: identifier,
          timerMs: 1200,
          siteName: themeConfig.theme?.branding?.siteName,
        })
        navigate('/', { replace: true })
        window.location.reload()
        return
      }
      setLoginError(res.message || res.error || 'เข้าสู่ระบบไม่สำเร็จ')
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = async () => {
    await authService.logout()
    navigate('/', { replace: true })
    window.location.reload()
  }

  const openPaymentRates = useCallback(() => {
    window.dispatchEvent(new CustomEvent('htd-open-payment-rates'))
  }, [])

  return (
    <div className="htd2" style={{ ['--htd2-bg-image' as string]: `url(${bgUrl})` }}>
      <div className="htd2-ambient" aria-hidden>
        <span className="htd2-ambient__orb htd2-ambient__orb--a" />
        <span className="htd2-ambient__orb htd2-ambient__orb--b" />
        <span className="htd2-ambient__orb htd2-ambient__orb--c" />
      </div>

      <header className="htd2-top htd2-anim htd2-anim--1">
        <Link to="/" className="htd2-top__brand" aria-label={siteName}>
          {logoUrl ? <img src={logoUrl} alt={siteName} onError={handleImgError} /> : <span>{siteName}</span>}
        </Link>
        <nav className="htd2-top__nav" aria-label="เมนูหลัก">
          <button
            type="button"
            className="htd2-top__rates payment-rates-cta payment-rates-cta--htd-top"
            onClick={openPaymentRates}
            aria-label="อัตราการจ่าย"
          >
            <i className="fas fa-coins payment-rates-cta__icon" aria-hidden />
            <span className="payment-rates-cta__label">
              <span className="htd2-top__btn-label htd2-top__btn-label--long">อัตราการจ่าย</span>
              <span className="htd2-top__btn-label htd2-top__btn-label--short">อัตราจ่าย</span>
            </span>
          </button>
          <Link to="/contact" className="htd2-top__pill htd2-top__pill--contact" aria-label="ติดต่อเรา">
            <i className="fab fa-line" aria-hidden />
            <span className="htd2-top__pill-text">
              <span className="htd2-top__btn-label htd2-top__btn-label--long">ติดต่อเรา</span>
              <span className="htd2-top__btn-label htd2-top__btn-label--short">ติดต่อ</span>
            </span>
          </Link>
          {!authenticated ? (
            <Link to="/register" className="htd2-top__pill htd2-top__pill--register" aria-label="สมัครสมาชิก">
              <i className="fas fa-user-plus htd2-top__pill-icon" aria-hidden />
              <span className="htd2-top__pill-text">
                <span className="htd2-top__btn-label htd2-top__btn-label--long">สมัครสมาชิก</span>
                <span className="htd2-top__btn-label htd2-top__btn-label--short">สมัคร</span>
              </span>
            </Link>
          ) : (
            <>
              <Link to="/lobby-huay" className="htd2-top__pill htd2-top__pill--register" aria-label="เข้าแทงหวย">
                <i className="fas fa-ticket-alt htd2-top__pill-icon" aria-hidden />
                <span className="htd2-top__pill-text">
                  <span className="htd2-top__btn-label htd2-top__btn-label--long">เข้าแทงหวย</span>
                  <span className="htd2-top__btn-label htd2-top__btn-label--short">แทงหวย</span>
                </span>
              </Link>
              <button type="button" className="htd2-top__pill htd2-top__pill--logout" onClick={handleLogout} aria-label="ออกจากระบบ">
                <i className="fas fa-sign-out-alt htd2-top__pill-icon" aria-hidden />
                <span className="htd2-top__pill-text">
                  <span className="htd2-top__btn-label htd2-top__btn-label--long">ออกจากระบบ</span>
                  <span className="htd2-top__btn-label htd2-top__btn-label--short">ออก</span>
                </span>
              </button>
            </>
          )}
        </nav>
      </header>

      <div className="htd2-marquee htd2-anim htd2-anim--1" role="region" aria-label="ประกาศ">
        <span className="htd2-marquee__badge" aria-hidden>
          <span className="htd2-marquee__pulse" />
          <span className="htd2-marquee__pulse htd2-marquee__pulse--delay" />
          <i className="fas fa-bullhorn" />
        </span>
        <div className="htd2-marquee__track-wrap">
          <div className="htd2-marquee__track">
            <span className="htd2-marquee__item">{marqueeText}</span>
            <span className="htd2-marquee__item" aria-hidden>
              {marqueeText}
            </span>
          </div>
        </div>
      </div>

      <section className="htd2-intro" aria-label="เข้าสู่ระบบ">
        <div className="htd2-intro__inner">
          <div className="htd2-intro__brand htd2-anim htd2-anim--2">
            <div className="htd2-intro__logo-wrap">
              <span className="htd2-intro__logo-glow" aria-hidden />
              {logoUrl ? (
                <img src={logoUrl} alt={siteName} className="htd2-intro__logo" onError={handleImgError} />
              ) : (
                <p className="htd2-intro__logo-fallback">{siteName}</p>
              )}
            </div>
            <p className="htd2-intro__tagline">หวยวันนี้ · มั่นคง ซื่อตรง จ่ายจริง</p>
            <div className="htd2-chips" aria-hidden>
              <span>รัฐบาล</span>
              <span>ยี่กี</span>
              <span>หุ้น</span>
              <span>ต่างประเทศ</span>
            </div>
          </div>

          <div className="htd2-intro__panel htd2-anim htd2-anim--3" id="htd2-login">
            {!authenticated ? (
              <form className="htd2-form" onSubmit={handleLogin}>
                <h1 className="htd2-form__title">เข้าสู่ระบบ</h1>
                <p className="htd2-form__sub">พร้อมแทงหวยได้ทันทีหลังเข้าสู่ระบบ</p>
                {loginError ? <p className="htd2-form__error">{loginError}</p> : null}
                <label className="htd2-field">
                  <span>ชื่อผู้ใช้ / เบอร์โทร</span>
                  <input
                    className="htd2-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loginLoading}
                    autoComplete="username"
                    placeholder="ชื่อผู้ใช้ หรือเบอร์โทร"
                  />
                </label>
                <label className="htd2-field">
                  <span>รหัสผ่าน</span>
                  <input
                    className="htd2-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loginLoading}
                    autoComplete="current-password"
                    placeholder="รหัสผ่าน"
                  />
                </label>
                <div className="htd2-form__meta">
                  <label className="htd2-remember">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    จำฉันไว้ในระบบ
                  </label>
                  <Link to="/contact" title="ติดต่อแอดมินเพื่อรีเซ็ตรหัสผ่าน">ลืมรหัสผ่าน</Link>
                </div>
                <button
                  type="submit"
                  className="htd2-btn htd2-btn--block htd2-btn--shine"
                  disabled={loginLoading || !username.trim() || !password}
                >
                  {loginLoading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
                </button>
                <p className="htd2-form__foot">
                  ยังไม่มีบัญชี? <Link to="/register">สมัครสมาชิก</Link>
                </p>
              </form>
            ) : (
              <>
                <div className="htd2-form htd2-form--member">
                  <div className="htd2-form__welcome">
                    <p className="htd2-form__hello">สวัสดีคุณ</p>
                    <p className="htd2-form__name">{user?.mobile || user?.username || '—'}</p>
                  </div>
                  <div className="htd2-balance">
                    {(() => {
                      const creditValue = loadingWallet
                        ? null
                        : walletService.getDisplayBalance(walletData)
                      const creditText =
                        creditValue == null
                          ? '…'
                          : walletService.formatCurrency(creditValue)
                      const pointsText = loadingWallet
                        ? '…'
                        : points.toLocaleString('th-TH', { maximumFractionDigits: 0 })
                      return (
                        <>
                          <div className="htd2-balance__tile htd2-balance__tile--credit">
                            <span className="htd2-balance__icon" aria-hidden>
                              <i className="fas fa-wallet" />
                            </span>
                            <div className="htd2-balance__meta">
                              <span>เครดิต</span>
                              <strong
                                className="htd2-balance__value"
                                title={creditValue == null ? undefined : `${creditText} บาท`}
                              >
                                <span className="htd2-balance__amount">{creditText}</span>
                                {creditValue != null ? (
                                  <span className="htd2-balance__unit">฿</span>
                                ) : null}
                              </strong>
                            </div>
                          </div>
                          <div className="htd2-balance__tile htd2-balance__tile--points">
                            <span className="htd2-balance__icon" aria-hidden>
                              <i className="fas fa-star" />
                            </span>
                            <div className="htd2-balance__meta">
                              <span>คะแนน</span>
                              <strong className="htd2-balance__value" title={pointsText}>
                                <span className="htd2-balance__amount">{pointsText}</span>
                              </strong>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                  <Link to="/points" className="htd2-form__footlink">
                    <i className="fas fa-gift" aria-hidden />
                    <span>ดูคะแนนทั้งหมด</span>
                    <i className="fas fa-chevron-right" aria-hidden />
                  </Link>
                </div>

                {isLotteryEnabled || isCasinoEnabled ? (
                  <div className="htd2-member-actions">
                    {isLotteryEnabled ? (
                      <Link to="/lobby-huay" className="htd2-member-action htd2-member-action--lottery">
                        <span className="htd2-member-action__icon" aria-hidden>
                          <i className="fas fa-ticket-alt" />
                        </span>
                        <span className="htd2-member-action__body">
                          <strong>เข้าแทงหวย</strong>
                          <small>เลือกหวยที่เปิดรับแทง</small>
                        </span>
                        <i className="fas fa-chevron-right htd2-member-action__arrow" aria-hidden />
                      </Link>
                    ) : null}
                    {isCasinoEnabled ? (
                      <Link to="/lobby-casino" className="htd2-member-action htd2-member-action--casino">
                        <span className="htd2-member-action__icon" aria-hidden>
                          <i className="fas fa-dice" />
                        </span>
                        <span className="htd2-member-action__body">
                          <strong>เข้าเล่นเกมส์</strong>
                          <small>คาสิโน · สล็อต · อื่นๆ</small>
                        </span>
                        <i className="fas fa-chevron-right htd2-member-action__arrow" aria-hidden />
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </section>

      <section
        className={`htd2-results${resultsVisible ? ' is-visible' : ''}`}
        id="htd2-all-results"
        aria-label="ผลรางวัลวันนี้"
        ref={resultsRef}
      >
        <div className="htd2-results__inner">
          <div className="htd2-results__head">
            <h2>
              <span className="htd2-live" aria-hidden />
              ผลรางวัลวันนี้
            </h2>
            <p>อัปเดตตามรอบที่ออกผล — ดูแล้วเข้าสู่ระบบเพื่อแทงหวย</p>
          </div>
          <div className="htd2-results__wrap">
            <HtdLotteryResults
              tenantId={themeConfig.tenantId}
              getAssetUrl={getAssetUrl}
              handleImgError={handleImgError}
              showPoolError
              wipWupMinutes={themeConfig.settings?.wipWupMinutes}
              wipWupMinutesOther={themeConfig.settings?.wipWupMinutesOther}
            />
          </div>
        </div>
      </section>
    </div>
  )
}

export default HtdHomePage
